/**
 * Decides whether a Cognito-authenticated user should be hydrated as an
 * EMPLOYEE (admin) session vs. a FACILITY (workplace) session.
 *
 * The authoritative signal is `custom:user_types` from the Cognito ID
 * token — the same attribute the auth Lambda gates on. Email-pattern
 * routing (via isCbhEmployeeEmail) was the historical proxy but
 * mis-classifies a legitimate sub-case: `_3p` suffix accounts (e.g.
 * `vendor_3p@clipboardhealth.com`) are third-party EMPLOYEE-typed users
 * intentionally exempted from SSO. The email pattern excluded them as
 * vendor accounts, sending them through the facility hydration path
 * where they bounced on "no associated facilities".
 *
 * Aliased employee emails (e.g. `user+facility1@clipboardhealth.com`)
 * remain an explicit "act as facility user" convention used by
 * employees for testing — the alias forces facility routing even when
 * user_types says employee.
 */
export function shouldHydrateAsEmployee(email: string, userTypesAttribute: unknown): boolean {
  if (typeof userTypesAttribute !== "string") {
    return false;
  }
  const userTypes = userTypesAttribute.split(",").map((type) => type.trim().toLowerCase());
  if (!userTypes.includes("employee")) {
    return false;
  }
  return !email.includes("+");
}
